import React, { useRef } from 'react'
import { useTranslate } from 'react-polyglot'
import delve from 'dlv'
import TweetTextButton from 'components/shared/TweetTextButton'
import { Title, Text, RichText } from 'components/typo'
import Typo from 'components/typo/Typo'
import Layout from 'components/shared/Layout'
import Sharer from 'components/shared/Sharer'
import Author from 'components/shared/Author'
import ArrowLink from 'components/shared/ArrowLink'
import Separator from 'components/shared/Separator'
import CoverImage from 'components/shared/CoverImage'
import Ratio from 'components/shared/Ratio'
import AuthorBlock from 'components/shared/AuthorBlock'
import Microdata from 'components/shared/Seo/Microdata'
import SectionsProvider from 'components/shared/sections/SectionsProvider'
import SectionsMap from 'components/shared/sections/SectionsMap'
import Link from 'src/components/shared/Link'

import readingTime from 'src/services/reading-time'
import useTweetText from 'src/hooks/useTweetText'
import useScrollTrigger from 'src/hooks/useScrollTrigger'
import { useSetProgressContext } from 'src/components/shared/ProgressProvider'
import DiscourseComments from 'src/components/shared/DiscourseComments'
import css from './styles.module.scss'
import TagLink from 'src/components/shared/TagLink'
import classnames from 'classnames/bind'

const cx = classnames.bind(css)
const imageRatio = 1 / 1.91

function getStrapiVersion(dateInput) {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput

  // Create Date objects for comparison
  const strapi4Start = new Date('2021-11-30')
  const strapi5Start = new Date('2024-09-23')

  // Check the date and return the corresponding Strapi version
  if (date > strapi5Start) {
    return '(Strapi 5 era)'
  } else if (date >= strapi4Start && date <= strapi5Start) {
    return '(Strapi v4 era)'
  } else if (date < strapi4Start) {
    return '(Strapi v3 era)'
  }
}

const ArticleContent = ({
  theme,
  title,
  subtitle,
  metaDescription,
  links,
  image,
  featuredCategory,
  introText,
  author,
  publishedAt,
  updatedAt,
  content,
  showAuthorBlock = false,
  showQuickInfos = false,
  comments = true,
  card,
  version,
  level,
  coauthors,
  tags,
}) => {
  const t = useTranslate()
  const setProgress = useSetProgressContext()

  const articleSupposedVersion = getStrapiVersion(updatedAt)

  const contentWrapperRef = useRef()
  const { onMouseUp: contentOnMouseUp, tweetState: contentTweetState } =
    useTweetText({
      textContainerRef: contentWrapperRef,
    })
  useScrollTrigger(contentWrapperRef, {
    onUpdate: (st) => {
      setProgress(st.progress)
    },
  })

  const headerRef = useRef()
  const { onMouseUp: headerOnMouseUp, tweetState: headerTweetState } =
    useTweetText({
      textContainerRef: headerRef,
    })

  const pageEntity = 'https://strapi.io/blog'

  return (
    <>
      <Microdata
        type="article"
        data={{
          name: title || metaDescription || '',
          description: metaDescription || '',
          image: delve(image, 'media.formats.thumbnail.url', ''),
          author: {
            name: delve(author, 'name', ''),
            image: delve(author, 'image.media.url', ''),
          },
          mainEntityOfPage: pageEntity,
          publishedAt,
          updatedAt,
        }}
      />
      <SectionsProvider>
        <Layout type="articleWrapper" className={css.wrapper}>
          {(title || subtitle) && (
            <div
              ref={headerRef}
              onMouseUp={headerOnMouseUp}
              className={css.header}
            >
              {showQuickInfos && (
                <Text className={css.quickInfos} theme="chambray" tag="ul">
                  {delve(featuredCategory, 'name') && (
                    <li className={css.featuredCategory}>
                      {featuredCategory.name}
                    </li>
                  )}
                  {updatedAt && (
                    <li>
                      {t('blog.last_updated', {
                        date: new Intl.DateTimeFormat('en', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        }).format(new Date(updatedAt)),
                      })}{' '}
                      {articleSupposedVersion}
                    </li>
                  )}
                  {content && (
                    <li className={css.readingTime}>
                      {t('blog.read_time', { value: readingTime(content) })}
                    </li>
                  )}
                </Text>
              )}
              {title && (
                <Title theme="bay-of-many" tag="h1" className={css.title}>
                  {title}
                </Title>
              )}
              {tags && tags.data?.length <= 0 && (
                <div className={css.tags}>
                  {version && (
                    <div className={css.versionTag}>
                      {version === 'v3' ? (
                        <Link
                          href={{
                            pathname: '/blog/categories/tutorials',
                            query: { type: version },
                          }}
                          className={css.v3}
                        >
                          Strapi v3
                        </Link>
                      ) : (
                        <Link
                          href={{
                            pathname: '/blog/categories/tutorials',
                            query: { type: version },
                          }}
                          className={css.v4}
                        >
                          Strapi v4
                        </Link>
                      )}
                    </div>
                  )}
                  {level && (
                    <div className={css.levelTag}>
                      <label className={css.level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </label>
                    </div>
                  )}
                </div>
              )}

              {tags && tags.data?.length > 0 && (
                <div
                  className={cx(
                    css.postTags,
                  )}
                >
                  {tags.data.map((tag) => (
                    <TagLink
                      key={tag.attributes.slug}
                      name={tag.attributes.name}
                      href={`/blog/tag/${tag.attributes.slug}`}
                    />
                  ))}
                </div>
              )}
              {subtitle && (
                <Text tag="h2" className={css.subtitle}>
                  {subtitle}
                </Text>
              )}
              {introText && (
                <Typo
                  type="IntroText"
                  className={css.introText}
                  theme="chambray"
                  tag="p"
                >
                  {introText}
                </Typo>
              )}
              {author?.name && (
                <div className={css.authorContainer}>
                  <Author
                    className={css.author}
                    theme={theme}
                    date={publishedAt}
                    updatedAt={updatedAt}
                    {...author}
                    coauthors={coauthors}
                  />
                  <Separator className={css.separator} />
                </div>
              )}
              <TweetTextButton {...headerTweetState} />
            </div>
          )}
        </Layout>
        {image && (
          <Layout type="articleWrapper" className={css.coverWrapper}>
            <Ratio className={css.articleCover} ratio={imageRatio}>
              <CoverImage {...image} />
            </Ratio>
          </Layout>
        )}
        <div className={css.fullWidthContentContainer}>
          <div className={css.sectionsMapContainer}>
            <SectionsMap />
          </div>
          <Layout type="articleWrapper" className={css.contentContainer}>
            <div className={css.sharerContainer}>
              <Sharer className={css.sharer} card={card} />
            </div>
            <div className={css.content}>
              <div ref={contentWrapperRef} onMouseUp={contentOnMouseUp}>
                <RichText type="Text" text={content} linksTheme="background" />
                <TweetTextButton {...contentTweetState} />
              </div>
              {links && (
                <ul className={css.links}>
                  {links.map(({ className, text, ...rest }, index) => {
                    return (
                      <ArrowLink
                        key={index}
                        tag="li"
                        theme={theme}
                        className={css.link}
                        {...rest}
                      >
                        {text}
                      </ArrowLink>
                    )
                  })}
                </ul>
              )}
            </div>
            <div className={css.deviceSharer}>
              <Sharer layout="row" />
            </div>
            {author && showAuthorBlock && (
              <AuthorBlock {...author} coauthors={coauthors} />
            )}
          </Layout>
        </div>
        <Layout type="articleWrapper">
          {comments && <DiscourseComments />}
        </Layout>
      </SectionsProvider>
    </>
  )
}

ArticleContent.defaultProps = {
  theme: 'purple',
  decoration: {
    variantId: 3,
    rotate: 180,
    bottom: true,
  },
}

export default ArticleContent
